import { Component, OnInit, inject, DestroyRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { translate } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest, interval, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { Blueprint } from 'src/app/models/blueprints/blueprint';
import { BreadcrumbItem } from 'src/app/models/breadcrumb/breadcrumb-item';
import { DeployStatus } from 'src/app/models/deploy/deploy-status';
import { EndpointType } from 'src/app/models/endpoint-type';
import { Instance } from 'src/app/models/instance/instance';
import { RouterSegments } from 'src/app/models/router-segments';
import { Solution } from 'src/app/models/service/solution';
import { BlueprintsService } from 'src/app/services/api/blueprints.service';
import { InstancesService } from 'src/app/services/api/instances.service';
import { NotificationsService } from 'src/app/services/api/notifications.service';
import { SolutionsService } from 'src/app/services/api/solutions.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ErrorPageService } from 'src/app/services/util/error-page.service';
import { TrialStatusService } from 'src/app/services/util/trial-status.service';
import { Capability } from '../../../models/service/capability';


@Component({
    selector: 'app-byob-blueprint-summary',
    templateUrl: './byob-blueprint-summary.component.html',
    styleUrls: ['./byob-blueprint-summary.component.scss'],
    standalone: false
})
export class ByobBlueprintSummaryComponent implements OnInit {
private destroyRef = inject(DestroyRef);
  extendSandbox = false;
  selectedCapabilities: Capability[];
  TOTAL_VALUE = 1 * (60*1000);
  DeployStatus = DeployStatus;
  tenant: string;
  breadcrumbItems: BreadcrumbItem[];
  service: Solution;
  showStepsLoadingSection = true;
  trialStatus: string;
  demoInstance: Instance;
  blueprint: Blueprint;
  dataLoaded: boolean;
  configTypeEndpoints: any;
  accessTypeEndpoints: any;
  exploreTypeEndpoints: any;
  otherTypeEndpoints: any;
  progressIntervalSubscription: Subscription;
  progressPercentage = 100;
  isSandbox: boolean;

  constructor(private activatedRoute: ActivatedRoute, private solutionsService: SolutionsService,
    private instancesService: InstancesService, private trialStatusService: TrialStatusService,
    private blueprintsService: BlueprintsService, private notificationsService: NotificationsService,
    private errorPageService: ErrorPageService,
    private router: Router, private authService: AuthService) {
    }

  ngOnInit(): void {
    this.loadData();
    this.tenant = this.authService.principal.provider;
    this.subscribeForNotification();
  }

  goToExtendSandbox(): void {
    this.router.navigate([this.tenant,RouterSegments.SERVICES,this.service.id,translate('BLUEPRINT_SUMMARY.EXTEND.EXTEND'), this.blueprint.id],
    {state: {installDoc: this.blueprint.installationDocument}});
  }

  openUrl(url): void {
    window.open(url, '_blank', 'noopener');
  }

  private checkIfDeployed(): void {
    if (this.progressPercentage === 100 && this.demoInstance.status === DeployStatus.DEPLOYED) {
      this.showStepsLoadingSection = false;
     }
  }

  private loadData(): void {
    const blueprintId = this.activatedRoute.snapshot.params.blueprintId;
    const serviceId = this.activatedRoute.snapshot.params.serviceId;
    combineLatest([this.solutionsService.getSolutionDetailsById(serviceId), this.blueprintsService.getBlueprint(blueprintId),
      this.instancesService.getInstancesForBlueprint(blueprintId), this.solutionsService.getSolutionVersions(serviceId)
    ]).pipe(
        finalize(() => this.dataLoaded = true),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(([service, blueprint, instancesForBlueprint, serviceVersions]) => {
        this.service = service;
        this.extendSandbox = blueprint.capabilities && serviceVersions[0].capabilities ? true : false;
        this.selectedCapabilities = (blueprint.capabilities) ;
        this.configureBreadcrumb(this.blueprint, this.service);
        this.blueprint = blueprint;
        this.demoInstance = instancesForBlueprint[0];
        this.getEndpointTypes(this.demoInstance);
        this.trialStatus = this.trialStatusService.getTrialStatus(this.demoInstance);
        this.calculateProgress();
        this.checkIfDeployed();
        if(this.progressPercentage < 100) {
          this.startProgress();
        }
      }, (err) => this.errorPageService.show404Page());
  }

  private calculateProgress(): void {
    this.progressPercentage = 50;
    const startDate = new Date(this.demoInstance.dateCreated).getTime();
    const now = new Date().getTime();
    const value = now - startDate;
    this.progressPercentage =  value / this.TOTAL_VALUE * 100;
    if (this.progressPercentage > 100) {
      this.progressPercentage = 100;
    }
  }

  private startProgress(): void {
    this.progressIntervalSubscription = interval(5000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.calculateProgress();
        if (this.progressPercentage === 100) {
          this.progressIntervalSubscription.unsubscribe();
          this.checkIfDeployed();
        }
    });
  }

  private getEndpointTypes(instance: Instance): void {
    this.configTypeEndpoints = instance.endpoints.filter(endpoint => endpoint.useType === EndpointType.CONFIGURATION);
    this.accessTypeEndpoints = instance.endpoints.filter(endpoint => endpoint.useType === EndpointType.UI);
    this.exploreTypeEndpoints = instance.endpoints.filter(endpoint => endpoint.useType === EndpointType.API);
    this.otherTypeEndpoints = instance.endpoints.filter(endpoint => endpoint.useType === EndpointType.OTHER);
  }

  private configureBreadcrumb(blueprint: Blueprint, service: Solution): void {
    this.breadcrumbItems = [];
    this.breadcrumbItems.push({ label: 'Home', url: [] });
    // this.breadcrumbItems.push({ label: translate('CATALOGUE.CATALOGUE'), url: ['catalogue'] });
    // this.breadcrumbItems.push({ label: service.name, url: ['services', service.id] });
    this.breadcrumbItems.push({ label: this.isSandbox ? blueprint.name : translate('BLUEPRINT_SUMMARY.EXPLORE_SANDBOX')});
  }

  private subscribeForNotification(): void {
    this.notificationsService.onNotification(
      { type: 'com.temenos.engine.instance', data: { status: 'created' } }
     )
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe((notification) => {
      if (notification.data?.id === this.demoInstance?.id) {
        this.instancesService.getInstanceById(this.demoInstance.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
          (instance) => {
            this.demoInstance = instance;
            this.getEndpointTypes(this.demoInstance);
            this.checkIfDeployed();
          });
      }
    });
  }

}
